import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const SCROLL_KEY = 'salesDashboard.scroll'
const CANCEL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab']
const CANCEL_EVENTS = ['wheel', 'touchmove', 'pointerdown', 'keydown']

const readSaved = (key, search) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null')
    return saved && saved.key === key && saved.search === search && Number.isFinite(saved.y) ? saved.y : null
  } catch {
    return null
  }
}

const forgetSaved = () => {
  try {
    sessionStorage.removeItem(SCROLL_KEY)
    return true
  } catch {
    return false
  }
}

const useDashboardScroll = ({ enabled, ready }) => {
  const { key, search } = useLocation()
  const target = useRef(undefined)
  const targetKey = useRef(null)

  if (enabled && target.current === undefined) {
    target.current = readSaved(key, search)
    targetKey.current = key
  }

  const pending = enabled && typeof target.current === 'number'

  useEffect(() => {
    if (!pending) return undefined
    const cancel = (event) => {
      if (typeof target.current !== 'number') return
      if (event.type === 'keydown' && !CANCEL_KEYS.includes(event.key)) return
      if (event.type === 'wheel' && Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      target.current = null
      forgetSaved()
    }
    CANCEL_EVENTS.forEach((type) => window.addEventListener(type, cancel, { passive: true }))
    return () => {
      CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, cancel, { passive: true }))
    }
  }, [pending])

  useEffect(() => {
    if (!enabled || typeof target.current !== 'number') return
    if (key !== targetKey.current) {
      target.current = null
      forgetSaved()
      return
    }
    if (!ready) return
    window.scrollTo(0, target.current)
    target.current = null
    forgetSaved()
  }, [enabled, ready, key])

  const save = useCallback(() => {
    target.current = null
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ key, search, y: window.scrollY }))
      return true
    } catch {
      return false
    }
  }, [key, search])

  return { save }
}

export default useDashboardScroll
